import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgbModalModule, NgbModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { ImageCropperModule } from 'ngx-image-cropper';
import { BaseAvatarComponent } from './components/avatar/base-avatar.component';
import { BaseSignContractsInfoCardComponent } from './components/base-assignment-sign-info-card/base-sign-contracts-info-card.component';
import { BaseSignContractsSignComponent } from './components/base-signature-sign/base-sign-contracts-sign.component';
import { BaseToastAlertItemComponent } from './components/base-toast-alert-item/base-toast-alert-item.component';
import { BaseToastAlertsComponent } from './components/base-toast-alerts/base-toast-alerts.component';
import { BaseUpToDateComponent } from './components/base-up-to-date/base-up-to-date.component';
import { BaseButtonComponent } from './components/button/base-button.component';
import { BaseCandidateListProfileComponent } from './components/candidate-list-profile/base-candidate-list-profile.component';
import { BaseCheckboxWeekdaysComponent } from './components/checkbox-weekdays/base-checkbox-weekdays.component';
import { BaseCheckboxComponent } from './components/checkbox/base-checkbox.component';
import { BaseDatepickerComponent } from './components/datepicker/base-datepicker.component';
import { BaseDocumentInfoComponent } from './components/document-info/base-document-info.component';
import { BaseDocumentUploadComponent } from './components/document-upload/base-document-upload.component';
import { BaseActionDropdownComponent } from './components/dropdowns/action-dropdown/base-action-dropdown.component';
import { BaseCountryDropdownComponent } from './components/dropdowns/country-dropdown/base-country-dropdown.component';
import { BaseKebabMenuComponent } from './components/dropdowns/kebab-menu/base-kebab-menu.component';
import { BaseFilterDropdownComponent } from './components/dropdowns/filter-dropdown/base-filter-dropdown.component';
import { BaseDropdownComponent } from './components/dropdowns/base-dropdown/base-dropdown.component';
import { BaseFilterButtonsComponent } from './components/filter-buttons/base-filter-buttons.component';
import { BaseFormFieldPreviewComponent } from './components/form-field-preview/base-form-field-preview.component';
import { BaseImageCropperComponent } from './components/image-cropper/base-image-cropper.component';
import { BaseInputComponent } from './components/input/base-input.component';
import { BaseLanguageSwitchComponent } from './components/language-switch/base-language-switch.component';
import { BaseModalComponent } from './components/modal/base-modal.component';
import { BaseXlsxCsvPreviewComponent } from './components/base-xlsx-csv-preview/base-xlsx-csv-preview.component';
import { BaseModalBodyComponent } from './components/modal/modal-body/base-modal-body.component';
import { BasePaginationComponent } from './components/pagination/base-pagination.component';
import { BasePasswordComponent } from './components/password/base-password.component';
import { BaseRadiobuttonComponent } from './components/radiobutton/base-radiobutton.component';
import { BaseRangeDatepickerComponent } from './components/range-datepicker/range-datepicker.component';
import { BaseRatingComponent } from './components/rating/base-rating.component';
import { BaseSearchComponent } from './components/search/base-search.component';
import { BaseSignContractSelectionComponent } from './components/sign-contract-selection/base-sign-contract-selection.component';
import { BaseSpecialButtonComponent } from './components/special-button/base-special-button.component';
import { BaseSwitchComponent } from './components/switch/base-switch.component';
import { BaseTextareaComponent } from './components/textarea/base-textarea.component';
import { BaseInfiniteScrollComponent } from './components/infinite-scroll/base-infinite-scroll.component';
import { BaseClickOutsideDirective } from './directives/base-click-outside.directive';
import { BaseCollapsableContentDirective } from './directives/base-collapsable-content.directive';
import { BaseImageFallbackDirective } from './directives/base-image-fallback.directive';
import { BaseIsStickyDirective } from './directives/base-is-sticky.directive';
import { BaseTextTruncateDirective } from './directives/base-text-truncate.directive';
import { BaseMaskDateDirective } from './directives/date-mask.directive';
import { BaseCountryPreviewValuePipe } from './pipes/base-country-preview-value.pipe';
import { BaseDateToMonthNamePipe } from './pipes/base-date-to-month-name.pipe';
import { BaseDaysDifferencePipe } from './pipes/base-days-difference.pipe';
import { BaseFieldValidationPipe } from './pipes/base-field-validation.pipe';
import { FilesizePipe } from './pipes/base-filesize.pipe';
import { BaseFilterPipe } from './pipes/base-filter.pipe';
import { BaseFormControlCastPipe } from './pipes/base-form-control-cast.pipe';
import { BaseFormatDatePipe } from './pipes/base-format-date.pipe';
import { BaseFormatDecimalPipe } from './pipes/base-format-decimal.pipe';
import { BaseFormatPricePipe } from './pipes/base-format-price.pipe';
import { BaseFormatTimePipe } from './pipes/base-format-time.pipe';
import { BaseIsEmptyPipe } from './pipes/base-is-empty.pipe';
import { BaseIsWeekendPipe } from './pipes/base-is-weekend.pipe';
import { BaseLongNamePipe } from './pipes/base-long-name.pipe';
import { BaseSoftHyphenatePipe } from './pipes/base-soft-hyphenate.pipe';
import { RelativeDateTimePipe } from './pipes/base-relative-date-time.pipe';
import { BaseBusinessKeyPipe } from './pipes/base-business-key.pipe';
import { BaseSpinnerComponent } from './system/spinner/base-spinner.component';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { BaseDynamicFontSizeDirective } from './directives/base-dynamic-font-size.directive';
import { BaseRemoveMailtoDirective } from './directives/base-remove-mailto.directive';
import { BaseDocumentViewerComponent } from './components/document-viewer/_components/document-viewer/base-document-viewer.component';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { BaseHoursToDaysPipe } from './pipes/base-hours-to-days.pipe';
import { BaseDepartmentQualificationsByCategoryPipe } from './pipes/base-department-qualifications-by-category.pipe';
import { BaseAnonymizeFullNamePipe } from './pipes/base-anonymize-full-name.pipe';
import { BaseMinutesToShortTimePipe } from './pipes/base-minutes-to-short-time.pipe';
import { BaseSignedMinutesPipe } from './pipes/base-signed-minutes.pipe';
import { CustomLabelComponent } from './components/dropdowns/base-dropdown/custom-label/custom-label.component';
import { BaseScrollToInvalidDirective } from './directives/base-scroll-to-invalid.directive';
import { BaseDocumentViewerToolbarComponent } from './components/document-viewer/_components/document-viewer-toolbar/base-document-viewer-toolbar.component';
import { BasePdfViewerComponent } from './components/document-viewer/base-pdf-viewer.component';
import { BaseImageViewerComponent } from './components/document-viewer/_components/image-viewer/base-image-viewer.component';
import { BaseShiftCheckExistingPipe } from './pipes/base-shift-check-existing.pipe';
import { BaseHorizontalScrollDirective } from './directives/base-horizontal-scroll.directive';
import { BaseShiftRadiobuttonComponent } from './components/base-shift-radiobutton/base-shift-radiobutton.component';
import { BaseBackButtonDirective } from './directives/base-back-button.directive';
import { BaseOrdinalSuffixPipe } from './pipes/base-ordinal-suffix.pipe';
import { BaseExpansionPanelComponent } from './components/base-expansion-panel/base-expansion-panel.component';
import { BaseCandidateWeekdaysAvailabilityComponent } from './components/base-candidate-weekdays-availability/base-candidate-weekdays-availability.component';
import { BaseCandidateShiftsAvailabilityComponent } from './components/base-candidate-shifts-availability/base-candidate-shifts-availability.component';
import { BaseServiceProviderDropdownComponent } from './components/dropdowns/service-provider-dropdown/base-service-provider-dropdown.component';
import { BaseMinuteInputComponent } from './components/base-minute-input/base-minute-input.component';
import { BaseBreakRuleSummaryComponent } from './components/base-break-rule-summary/base-break-rule-summary.component';
import { BaseNoConnectionBannerComponent } from './components/no-connection-banner/base-no-connection-banner.component';
import { BaseBranchesDropdownComponent } from './components/dropdowns/branches-dropdown/base-branches-dropdown.component';
import { BaseInputNumberComponent } from './components/base-input-number/base-input-number.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    NgbModule,
    NgbModalModule,
    NgbTooltipModule,
    NgSelectModule,
    NgOptimizedImage,
    NgxExtendedPdfViewerModule,
    TranslateModule,
    ImageCropperModule,
    InfiniteScrollDirective,
    LoadingBarModule,
    ScrollingModule,
  ],
  declarations: [
    BaseAvatarComponent,
    BaseModalComponent,
    BaseXlsxCsvPreviewComponent,
    BaseButtonComponent,
    BaseCheckboxWeekdaysComponent,
    BaseCheckboxComponent,
    BaseDatepickerComponent,
    BaseDocumentUploadComponent,
    BaseDocumentInfoComponent,
    BaseFilterButtonsComponent,
    BaseFilterDropdownComponent,
    BaseFormFieldPreviewComponent,
    BaseImageCropperComponent,
    BaseInputComponent,
    BasePaginationComponent,
    BasePasswordComponent,
    BaseDocumentViewerComponent,
    BaseDocumentViewerToolbarComponent,
    BasePdfViewerComponent,
    BaseImageViewerComponent,
    BaseRadiobuttonComponent,
    BaseSearchComponent,
    BaseSpecialButtonComponent,
    BaseSwitchComponent,
    BaseTextareaComponent,
    BaseModalBodyComponent,
    BaseClickOutsideDirective,
    BaseTextTruncateDirective,
    BaseMaskDateDirective,
    BaseRemoveMailtoDirective,
    BaseScrollToInvalidDirective,
    BaseSpinnerComponent,
    BaseFormatDecimalPipe,
    BaseFormatPricePipe,
    BaseFieldValidationPipe,
    BaseFilterPipe,
    BaseFormatDatePipe,
    BaseFormControlCastPipe,
    BaseLongNamePipe,
    BaseSoftHyphenatePipe,
    BaseDateToMonthNamePipe,
    BaseIsStickyDirective,
    BaseFormatTimePipe,
    BaseDaysDifferencePipe,
    BaseIsWeekendPipe,
    RelativeDateTimePipe,
    BaseLanguageSwitchComponent,
    BaseSignContractsInfoCardComponent,
    BaseSignContractSelectionComponent,
    BaseSignContractsSignComponent,
    BaseToastAlertsComponent,
    BaseToastAlertItemComponent,
    BaseCandidateListProfileComponent,
    BaseRatingComponent,
    FilesizePipe,
    BaseCollapsableContentDirective,
    BaseIsEmptyPipe,
    BaseRangeDatepickerComponent,
    BaseCountryPreviewValuePipe,
    BaseUpToDateComponent,
    BaseImageFallbackDirective,
    BaseDropdownComponent,
    BaseActionDropdownComponent,
    BaseKebabMenuComponent,
    BaseCountryDropdownComponent,
    CustomLabelComponent,
    BaseInfiniteScrollComponent,
    BaseDynamicFontSizeDirective,
    BaseHoursToDaysPipe,
    BaseDepartmentQualificationsByCategoryPipe,
    BaseAnonymizeFullNamePipe,
    BaseMinutesToShortTimePipe,
    BaseSignedMinutesPipe,
    BaseShiftCheckExistingPipe,
    BaseBusinessKeyPipe,
    BaseHorizontalScrollDirective,
    BaseShiftRadiobuttonComponent,
    BaseBackButtonDirective,
    BaseOrdinalSuffixPipe,
    BaseExpansionPanelComponent,
    BaseCandidateWeekdaysAvailabilityComponent,
    BaseCandidateShiftsAvailabilityComponent,
    BaseServiceProviderDropdownComponent,
    BaseMinuteInputComponent,
    BaseBreakRuleSummaryComponent,
    BaseNoConnectionBannerComponent,
    BaseBranchesDropdownComponent,
    BaseInputNumberComponent,
  ],
  exports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    NgbModalModule,
    RouterModule,
    TranslateModule,
    NgbTooltipModule,
    NgSelectModule,
    ScrollingModule,
    NgOptimizedImage,
    InfiniteScrollDirective,
    BaseAvatarComponent,
    BaseModalComponent,
    BaseXlsxCsvPreviewComponent,
    BaseButtonComponent,
    BaseCheckboxWeekdaysComponent,
    BaseCheckboxComponent,
    BaseDatepickerComponent,
    BaseDocumentUploadComponent,
    BaseDocumentInfoComponent,
    BaseFilterButtonsComponent,
    BaseFilterDropdownComponent,
    BaseFormFieldPreviewComponent,
    BaseImageCropperComponent,
    BaseInputComponent,
    BasePaginationComponent,
    BasePasswordComponent,
    BaseDocumentViewerComponent,
    BasePdfViewerComponent,
    BaseRadiobuttonComponent,
    BaseSearchComponent,
    BaseSpecialButtonComponent,
    BaseSwitchComponent,
    BaseTextareaComponent,
    BaseModalBodyComponent,
    BaseSpinnerComponent,
    BaseClickOutsideDirective,
    BaseFormatPricePipe,
    BaseFormatDecimalPipe,
    BaseFieldValidationPipe,
    BaseFilterPipe,
    BaseFormatDatePipe,
    BaseFormControlCastPipe,
    BaseLongNamePipe,
    BaseSoftHyphenatePipe,
    BaseIsStickyDirective,
    BaseTextTruncateDirective,
    BaseDateToMonthNamePipe,
    BaseFormatTimePipe,
    BaseDaysDifferencePipe,
    BaseIsWeekendPipe,
    BaseLanguageSwitchComponent,
    BaseSignContractSelectionComponent,
    BaseSignContractsSignComponent,
    BaseToastAlertsComponent,
    BaseToastAlertItemComponent,
    BaseSignContractsInfoCardComponent,
    BaseCandidateListProfileComponent,
    TranslatePipe,
    BaseRatingComponent,
    FilesizePipe,
    BaseCollapsableContentDirective,
    BaseIsEmptyPipe,
    RelativeDateTimePipe,
    BaseRangeDatepickerComponent,
    BaseCountryPreviewValuePipe,
    BaseUpToDateComponent,
    BaseImageFallbackDirective,
    BaseDropdownComponent,
    BaseActionDropdownComponent,
    BaseKebabMenuComponent,
    BaseCountryDropdownComponent,
    CustomLabelComponent,
    BaseInfiniteScrollComponent,
    BaseDynamicFontSizeDirective,
    BaseHoursToDaysPipe,
    BaseDepartmentQualificationsByCategoryPipe,
    BaseAnonymizeFullNamePipe,
    BaseMinutesToShortTimePipe,
    BaseSignedMinutesPipe,
    BaseShiftCheckExistingPipe,
    BaseScrollToInvalidDirective,
    BaseBusinessKeyPipe,
    BaseHorizontalScrollDirective,
    BaseShiftRadiobuttonComponent,
    BaseBackButtonDirective,
    BaseOrdinalSuffixPipe,
    BaseExpansionPanelComponent,
    BaseCandidateWeekdaysAvailabilityComponent,
    BaseCandidateShiftsAvailabilityComponent,
    BaseServiceProviderDropdownComponent,
    BaseMinuteInputComponent,
    BaseBreakRuleSummaryComponent,
    BaseNoConnectionBannerComponent,
    BaseBranchesDropdownComponent,
    BaseInputNumberComponent,
  ],
})
export class BaseSharedModule {}
